import { useLocation } from "react-router-dom";
import { routes } from "constants/routes";
import { CompanyRole, COMPANY_TYPES, NOTIFICATION_TYPE_ENUM, UserListEntity, UserScope } from "interfaces";
import { Col, Flex, Form, Typography } from "antd";
import { CustomButton, MultiplySelect } from "ui";
import { _isRequired, getAvailableUserRoles, getCompanyType, getPhone, parseErrorMessage } from "utils";
import { FORM_VALIDATION_DEBOUNCE } from "constants/form";
import { useState } from "react";
import { useStores } from "stores";
import { useNotificationContext } from "providers";
import { editUserRoles } from "services";

interface IProps {
  roles: UserScope[];
  companyInfo: {
    id: string;
    role: CompanyRole;
    companyName: string;
    companyRegistrationNumber: string;
  };
  userInfo: UserListEntity;
}

export const EditRolesModal = (props: IProps) => {
  const { roles, companyInfo, userInfo } = props;
  const location = useLocation();
  const { actionModals, companyUsers, userCompanyList } = useStores();
  const { openNotification } = useNotificationContext();

  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);

  const isUserPage = location.pathname.startsWith(`/${routes.users}/`);

  const availableRoles = getAvailableUserRoles(companyInfo.role);

  const registrationLabel =
    getCompanyType(companyInfo.companyRegistrationNumber) === COMPANY_TYPES.IP ? "ОГРНИП" : "ОГРН";

  const editUserRolesHandler = async () => {
    await editUserRoles({ userId: userInfo.id }, { companyId: companyInfo.id, roles: form.getFieldValue("roles") })
      .then(() => {
        openNotification({
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
          message: "Роли успешно обновлены",
        });
        setTimeout(() => {
          if (isUserPage) {
            userCompanyList.fetch();
          } else {
            companyUsers.fetch();
          }
        }, 1500);
        handleCloseModal();
      })
      .catch((e) => {
        openNotification({
          description: parseErrorMessage(e),
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCloseModal = () => {
    actionModals.closeAll();
  };

  return (
    <Flex vertical gap="large">
      <Flex vertical gap="small">
        {isUserPage ? (
          <>
            <Typography.Text>
              Название компании: <Typography.Text strong>{companyInfo.companyName}</Typography.Text>
            </Typography.Text>
            <Typography.Text>
              {registrationLabel}: <Typography.Text strong>{companyInfo.companyRegistrationNumber}</Typography.Text>
            </Typography.Text>
          </>
        ) : userInfo ? (
          <>
            <Typography.Text>
              ФИО:{" "}
              <Typography.Text strong>
                {userInfo.surname} {userInfo.name} {userInfo.secondName ?? ""}
              </Typography.Text>
            </Typography.Text>
            {userInfo?.phone && (
              <Typography.Text>
                Телефон: <Typography.Text strong>{getPhone(userInfo.phone)}</Typography.Text>
              </Typography.Text>
            )}
          </>
        ) : null}
      </Flex>
      <Form
        form={form}
        name="editWallet"
        initialValues={{
          roles,
        }}
        onFinish={() => editUserRolesHandler()}
      >
        <Flex vertical gap="large">
          <Form.Item
            name="roles"
            className="full-width"
            rules={[_isRequired("Выберите хотя бы одну роль")]}
            validateDebounce={FORM_VALIDATION_DEBOUNCE}
          >
            <MultiplySelect placeholder="Роли пользователя" disabled={loading} options={availableRoles} />
          </Form.Item>
          <Form.Item>
            <Flex gap="large">
              <Col>
                <CustomButton htmlType="submit" loading={loading} disabled={loading}>
                  Сохранить
                </CustomButton>
              </Col>
              <Col>
                <CustomButton variant="outlined" loading={loading} disabled={loading} onClick={handleCloseModal}>
                  Отмена
                </CustomButton>
              </Col>
            </Flex>
          </Form.Item>
        </Flex>
      </Form>
    </Flex>
  );
};
